import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { BulkMessageModel, MessageModel, MetaModel, MetaPagingModel, ParticipantModel, RoomModel } from '@shared/models';
import { BulkMessage, Message, Participant, Room } from '@shared/factories';
import { ChatEventsService, QueryCollectorService, UserService } from '@shared/services';

import { environment } from 'environments/environment';

interface CountersResponce     { unread:        number;                                    }
interface RoomsResponce        { rooms:         RoomModel[];        meta: MetaPagingModel; }
interface MessagesResponce     { messages:      MessageModel[];     meta: MetaModel;       }
interface BulkMessagesResponce { bulk_messages: BulkMessageModel[]; meta: MetaPagingModel; }
interface MessageResponce      { message:       MessageModel;                              }
interface BulkMessageResponce  { bulk_message:  BulkMessageModel;                          }
interface ParticipantResponce  { participant:   ParticipantModel;                          }

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private get CHAT_API():  string { return `${environment.apiUrl}api/portal/v3/chat` };
  private     ROOM_API:    string = `${this.CHAT_API}/rooms`;
  private     BULK_API:    string = `${this.CHAT_API}/bulk_messages`;
  private     COUNTER_API: string = `${this.CHAT_API}/messages_counters`;

  constructor(
    private http:                  HttpClient,
    private queryCollectorService: QueryCollectorService,
    private chatEventsService:     ChatEventsService,
    private userservice:           UserService,
  ) { }

  getTotalUnreadCount(): Observable<number> {
    return this.http.get<CountersResponce>(`${this.COUNTER_API}${this.queryCollectorService.getCountersQuery('chat_feature')}`).pipe(
      take(1),
      map(res => res?.unread),
      tap(count => this.chatEventsService.changeTotalUnreadCount(count))
    );
  }

  loadRoomById(id: number): Observable<Room> {
    return this.http.get<any>(`${this.ROOM_API}/${id}`).pipe(
      take(1),
      map(res => res?.room ? new Room(res?.room, this.userservice.currentUserValue.id) : null )
    );
  }

  loadRooms(page: number = 1, search: string = null): Observable<{ rooms: Room[], meta: MetaModel }> {
    return this.http.get<RoomsResponce>(`${this.ROOM_API}${this.queryCollectorService.getRoomsQuery(page, search)}`).pipe(
      take(1),
      map(res => ({
        rooms: res?.rooms?.map(u => new Room(u, this.userservice.currentUserValue.id)),
        meta:  res?.meta?.paging
      }))
    );
  }

  joinRooom(roomId: number): Observable<Participant> {
    return this.http.post<ParticipantResponce>(`${this.ROOM_API}/${roomId}/participants/join`, {}).pipe(
      map(res => res?.participant),
      map(p => new Participant(p))
    );
  }

  leaveRooom(roomId: number): Observable<void> {
    return this.http.post<any>(`${this.ROOM_API}/${roomId}/participants/leave`, {});
  }

  loadMessages(roomId: number, page: number = 1): Observable<Message[]> {
    return this.http.get<MessagesResponce>(`${this.ROOM_API}/${roomId}/messages${this.queryCollectorService.getMessagesQuery(page)}`).pipe(
      take(1),
      map(res => res.messages),
      map(res => res.map((m => new Message(m, this.userservice.currentUserValue.id))))
    );
  }

  loadBulkMessages(page: number = 1, search: string = '', kinds: string[] = []): Observable<{ totalPages: number, meta: MetaModel, messages: BulkMessage[] }> {
    return this.http.get<BulkMessagesResponce>(`${this.BULK_API}${this.queryCollectorService.getBulkMessagesQuery(page, search, kinds)}`).pipe(
      map(res => ({
        totalPages: res?.meta?.paging?.total_pages,
        meta:       res?.meta?.paging,
        messages:   res?.bulk_messages.map(c => new BulkMessage(c))
      }))
    );
  }

  markAllAsRead(roomId: number): Observable<void> {
    return this.http.post<void>(`${this.ROOM_API}/${roomId}/messages/read_all`, {}).pipe(
      switchMap(() => this.getTotalUnreadCount()),
      tap(() => this.chatEventsService.markAllAsReadCallback(roomId)),
      map(() => void 0)
    );
  }

  postMessage(  roomId: number,                    text: string): Observable<Message> { return this.messageHandler('post',   roomId, text           ) as Observable<Message>; }
  updateMessage(roomId: number, messageId: number, text: string): Observable<Message> { return this.messageHandler('put',    roomId, text, messageId) as Observable<Message>; }
  deleteMessage(roomId: number, messageId: number              ): Observable<void>    { return this.messageHandler('delete', roomId, null, messageId) as Observable<void>;    }

  postBulkMessage(  text: string, type: string, ids: number[]): Observable<BulkMessage> { return this.messageBulkHandler('post',   text, type, ids            ) as Observable<BulkMessage>; }
  updateBulkMessage(text: string, messageId: number          ): Observable<BulkMessage> { return this.messageBulkHandler('put',    text, null, null, messageId) as Observable<BulkMessage>; }
  deleteBulkMessage(              messageId: number          ): Observable<void>        { return this.messageBulkHandler('delete', null, null, null, messageId) as Observable<void>;        }

  private messageHandler(action: string, roomId: number, text: string, messageId: number = null): Observable<Message | void> {
    let url  = `${this.ROOM_API}/${roomId}/messages`;
    let body = { message: { text } };
    let call = this.prepareCall(action, url, body, messageId);

    return call.pipe(map(res => (res as MessageResponce)?.message ? new Message((res as MessageResponce)?.message, this.userservice.currentUserValue.id) : null));
  }

  private messageBulkHandler(action: string, text: string, type: string, ids: number[] = null, messageId: number = null): Observable<BulkMessage | void> {
    let body: any = { bulk_message: { text }};
    if (type) body.bulk_message.kind         = type;
    if (ids ) body.bulk_message[type+'_ids'] = ids.join(',');

    let call = this.prepareCall(action, this.BULK_API, body, messageId);

    return call.pipe(map(res => (res as BulkMessageResponce)?.bulk_message ? new BulkMessage((res as BulkMessageResponce)?.bulk_message) : null));
  }

  private prepareCall(action: string, url: string, body: any = null, messageId: number = null): Observable<MessageResponce | BulkMessageResponce | void> {
    if (action === 'put'   ) url += `/${messageId}`;
    if (action === 'delete') url += `/${messageId}`;

    if (action === 'post')   return this.http.post<MessageResponce | BulkMessageResponce>(url, body);
    if (action === 'put')    return this.http.put< MessageResponce | BulkMessageResponce>(url, body);
    if (action === 'delete') return this.http.delete<void>(url);
  }

}
